import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  List,
  message,
  Modal,
  Progress,
  Row,
  Select,
  Skeleton,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SendOutlined,
  ShopOutlined,
  SwapOutlined,
  TeamOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import SubscriptionStatusBadge from "./components/SubscriptionStatusBadge";
import { useSelection } from "../SelectionContext";
import {
  formatSubscriptionAmount,
  formatSubscriptionDate,
  formatSubscriptionNumber,
} from "./subscriptionFormatters";
import "./SubscriberSubscriptionPortal.css";

const { Paragraph, Text, Title } = Typography;
const { TextArea } = Input;

type SubscriptionStatus = "active" | "expired" | "trial" | "suspended";
type BillingFrequency = "monthly" | "quarterly" | "semiannual" | "annual";
type RequestType = "plan_change" | "renewal";
type RequestStatus = "pending" | "processed" | "rejected" | "cancelled";

interface SubscriberCompany {
  id: number;
  name: string;
  short_name: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  trial_ends_at: string | null;
  licence_expire_on: string | null;
  subscription_plan_id: number | null;
  package_type: BillingFrequency | null;
  subscription_status: SubscriptionStatus;
  days_remaining: number | null;
  total_users: number;
  active_users: number;
  active_warehouse_count: number;
  plan_name: string | null;
  plan_description: string | null;
  plan_price: number | null;
  plan_currency: string | null;
  plan_billing_cycle: string | null;
  plan_features: string[];
  plan_max_users: number | null;
  plan_max_companies: number | null;
  plan_storage_limit: number | null;
}

interface BillingSummary {
  total_invoices: number;
  paid_invoices: number;
  overdue_invoices: number;
  outstanding_amount: number;
  overdue_amount: number;
}

const billingFrequencyLabels: Record<BillingFrequency, string> = {
  monthly: "mensuelle",
  quarterly: "trimestrielle",
  semiannual: "semestrielle",
  annual: "annuelle",
};

interface SubscriberInvoice {
  id: number;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  period_start: string;
  period_end: string;
  total_amount: number;
  paid_amount: number;
  balance_due: number;
  currency: string;
  status: string;
  display_status: string;
  payment_status: string;
  payment_date: string | null;
  plan_name: string | null;
  replaces_invoice_id?: number | null;
  replaces_invoice_number?: string | null;
  replacement_invoice_id?: number | null;
  replacement_invoice_number?: string | null;
  correction_reason?: string | null;
  corrected_at?: string | null;
}

interface AvailablePlan {
  id: number;
  name: string;
  description: string | null;
  price: number;
  currency: string;
  billing_cycle: string;
  features: string[];
  max_users: number | null;
  max_companies: number | null;
  storage_limit_gb: number | null;
  is_trial: boolean;
  trial_duration_days: number;
  sort_order: number;
}

interface SubscriptionRequest {
  id: number;
  request_type: RequestType;
  requested_plan_id: number | null;
  requested_duration_months: number | null;
  message: string | null;
  status: RequestStatus;
  review_note: string | null;
  created_at: string;
  reviewed_at: string | null;
  requested_plan_name: string | null;
}

interface SubscriptionAccess {
  access_state: "normal" | "warning" | "read_only" | "suspended";
  reason: string | null;
  outstanding_amount: number;
  overdue_days: number;
  credit_limit: number;
  days_in_read_only?: number;
  days_before_suspension?: number;
  access_enforcement_enabled: boolean;
  restriction_enforced?: boolean;
}

interface PortalData {
  company: SubscriberCompany;
  billing: BillingSummary;
  invoices: SubscriberInvoice[];
  available_plans: AvailablePlan[];
  requests: SubscriptionRequest[];
  access: SubscriptionAccess | null;
}

const invoiceStatusLabels: Record<string, string> = {
  draft: "Brouillon",
  issued: "Émise",
  paid: "Payée",
  overdue: "En retard",
  cancelled: "Annulée",
  refunded: "Remboursée",
};

const invoiceStatusColors: Record<string, string> = {
  draft: "default",
  issued: "processing",
  paid: "success",
  overdue: "error",
  cancelled: "default",
  refunded: "purple",
};

const requestStatusLabels: Record<RequestStatus, string> = {
  pending: "En attente",
  processed: "Traitée",
  rejected: "Refusée",
  cancelled: "Annulée",
};

const requestStatusColors: Record<RequestStatus, string> = {
  pending: "processing",
  processed: "success",
  rejected: "error",
  cancelled: "default",
};

const getPlanLimit = (value: number | null, suffix = "") =>
  value ? `${formatSubscriptionNumber(value)}${suffix}` : "Illimité";

const expirationReasons = new Set([
  "expired",
  "subscription_expired",
  "trial_expired",
]);

const getAccessAlertContent = (
  access: SubscriptionAccess,
  currency: string | null,
  billing: BillingSummary,
) => {
  const isTrialExpired = access.reason === "trial_expired";
  const isExpirationRestriction = expirationReasons.has(access.reason || "");
  const restrictionEnforced = Boolean(
    access.restriction_enforced ??
      (isExpirationRestriction || access.access_enforcement_enabled),
  );

  if (isExpirationRestriction) {
    if (access.access_state === "read_only") {
      const remainingDays = Math.max(0, access.days_before_suspension || 0);
      return {
        type: "error" as const,
        message: isTrialExpired
          ? "Période d’essai terminée — accès en lecture seule"
          : "Souscription expirée — accès en lecture seule",
        description:
          `${isTrialExpired ? "Votre période d’essai est terminée" : "Votre couverture est arrivée à échéance"}. ` +
          `Vous pouvez encore consulter vos données pendant ${remainingDays} jour(s), mais les créations, modifications et suppressions sont bloquées. ` +
          "Ouvrez l’espace Souscription pour régulariser la situation.",
      };
    }

    return {
      type: "error" as const,
      message: isTrialExpired
        ? "Période d’essai terminée — souscription requise"
        : "Souscription expirée — règlement requis",
      description: isTrialExpired
        ? "La période de consultation après l’essai est terminée. Seul l’espace Souscription reste accessible pour choisir une offre."
        : "La période de consultation après l’échéance est terminée. Seul l’espace Souscription reste accessible jusqu’à la régularisation du compte.",
    };
  }

  const overdueDays = `${formatSubscriptionNumber(access.overdue_days)} jour${access.overdue_days > 1 ? "s" : ""}`;
  const overdueDescription =
    billing.overdue_invoices === 1
      ? `Cette facture reste impayée, avec ${overdueDays} de retard. ` +
        `Le montant restant dû est de ${formatSubscriptionAmount(billing.overdue_amount, currency)}.`
      : billing.overdue_invoices > 1
        ? `${formatSubscriptionNumber(billing.overdue_invoices)} factures restent impayées après échéance, ` +
          `pour un total restant dû de ${formatSubscriptionAmount(billing.overdue_amount, currency)}. ` +
          `La facture la plus en retard a dépassé son échéance de ${overdueDays}.`
        : "";
  const financialDescription =
    `Encours : ${formatSubscriptionAmount(access.outstanding_amount, currency)}.` +
    (overdueDescription ? ` ${overdueDescription}` : "");
  if (access.access_state === "warning") {
    return {
      type: "warning" as const,
      message: "Règlement de souscription en retard",
      description: overdueDescription || financialDescription,
    };
  }

  return {
    type: "error" as const,
    message:
      access.access_state === "read_only"
        ? restrictionEnforced
          ? "Souscription limitée en lecture seule"
          : "Seuil de lecture seule détecté"
        : restrictionEnforced
          ? "Souscription suspendue"
          : "Votre délai de paiement des factures est dépassé ; votre compte pourrait être désactivé",
    description:
      financialDescription +
      (restrictionEnforced
        ? " Régularisez depuis l’onglet Factures pour rétablir la situation."
        : " Le diagnostic est affiché à titre d’observation, sans blocage automatique."),
  };
};

const getPortalRequestHeaders = (selectedCompany: unknown) => {
  const companyId = Number(selectedCompany);
  const headers: Record<string, string> = {
    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
    "Content-Type": "application/json",
  };

  if (Number.isSafeInteger(companyId) && companyId > 0) {
    headers["X-Company-Id"] = String(companyId);
  }

  return headers;
};

const SubscriberSubscriptionPortal: React.FC = () => {
  const { selectedCompany } = useSelection();
  const [portalData, setPortalData] = useState<PortalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestType, setRequestType] = useState<RequestType>("plan_change");
  const [submitting, setSubmitting] = useState(false);
  const [cancellingRequestId, setCancellingRequestId] = useState<number | null>(
    null,
  );
  const [selectedInvoice, setSelectedInvoice] =
    useState<SubscriberInvoice | null>(null);
  const [previewingInvoiceId, setPreviewingInvoiceId] = useState<number | null>(
    null,
  );
  const [form] = Form.useForm();

  const getRequestHeaders = useCallback(
    () => getPortalRequestHeaders(selectedCompany),
    [selectedCompany],
  );

  useEffect(() => {
    // Ne jamais conserver à l'écran une facture ou une demande appartenant au
    // contexte précédent pendant le chargement de la nouvelle entreprise.
    setPortalData(null);
    setSelectedInvoice(null);
    setPreviewingInvoiceId(null);
    setRequestModalOpen(false);
  }, [selectedCompany]);

  const loadPortal = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    try {
      const response = await fetch("/api/subscription/me", {
        headers: getRequestHeaders(),
        signal,
      });
      const payload = await response.json();

      if (!response.ok || !payload.success) {
        throw new Error(payload.message || "Impossible de charger la souscription");
      }

      setPortalData(payload.data);
    } catch (error) {
      if ((error as Error).name !== "AbortError") {
        message.error(
          error instanceof Error
            ? error.message
            : "Impossible de charger votre souscription",
        );
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [getRequestHeaders]);

  useEffect(() => {
    const controller = new AbortController();
    loadPortal(controller.signal);
    return () => controller.abort();
  }, [loadPortal]);

  const openRequestModal = useCallback(
    (type: RequestType, planId?: number) => {
      setRequestType(type);
      form.resetFields();
      form.setFieldsValue({
        request_type: type,
        requested_plan_id: planId,
        requested_duration_months: type === "renewal" ? 12 : undefined,
      });
      setRequestModalOpen(true);
    },
    [form],
  );

  const submitRequest = useCallback(
    async (values: {
      requested_plan_id?: number;
      requested_duration_months?: number;
      message?: string;
    }) => {
      setSubmitting(true);
      try {
        const response = await fetch("/api/subscription/me/requests", {
          method: "POST",
          headers: getRequestHeaders(),
          body: JSON.stringify({ request_type: requestType, ...values }),
        });
        const payload = await response.json();

        if (!response.ok || !payload.success) {
          throw new Error(payload.message || "Impossible d'envoyer la demande");
        }

        message.success(payload.message);
        setRequestModalOpen(false);
        form.resetFields();
        await loadPortal();
      } catch (error) {
        message.error(
          error instanceof Error
            ? error.message
            : "Impossible d'envoyer la demande",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [form, getRequestHeaders, loadPortal, requestType],
  );

  const cancelRequest = useCallback(
    async (requestId: number) => {
      setCancellingRequestId(requestId);
      try {
        const response = await fetch(
          `/api/subscription/me/requests/${requestId}/cancel`,
          {
            method: "PUT",
            headers: getRequestHeaders(),
          },
        );
        const payload = await response.json();

        if (!response.ok || !payload.success) {
          throw new Error(payload.message || "Impossible d'annuler la demande");
        }

        message.success(payload.message);
        await loadPortal();
      } catch (error) {
        message.error(
          error instanceof Error
            ? error.message
            : "Impossible d'annuler la demande",
        );
      } finally {
        setCancellingRequestId(null);
      }
    },
    [getRequestHeaders, loadPortal],
  );

  const openInvoicePdf = useCallback(
    async (invoice: SubscriberInvoice) => {
      const previewWindow = window.open("", "_blank");
      if (!previewWindow) {
        message.warning(
          "Impossible d'ouvrir l'aperçu. Autorisez les fenêtres pop-up pour ELSA GESTION.",
        );
        return;
      }

      previewWindow.document.title = "Préparation de la facture";
      previewWindow.document.body.style.margin = "0";
      previewWindow.document.body.style.minHeight = "100vh";
      previewWindow.document.body.style.display = "grid";
      previewWindow.document.body.style.placeItems = "center";
      previewWindow.document.body.style.fontFamily = "Inter, Arial, sans-serif";
      previewWindow.document.body.textContent = "Préparation de la facture…";

      setPreviewingInvoiceId(invoice.id);
      try {
        const response = await fetch(
          `/api/subscription/me/invoices/${invoice.id}/pdf`,
          { headers: getRequestHeaders() },
        );
        if (!response.ok) {
          let errorMessage = "Impossible de générer cette facture";
          try {
            const payload = await response.json();
            errorMessage = payload.message || errorMessage;
          } catch (_error) {}
          throw new Error(errorMessage);
        }

        const pdfBlob = await response.blob();
        const fileName = `Facture-${invoice.invoice_number}.pdf`;
        const pdfFile = new File([pdfBlob], fileName, {
          type: pdfBlob.type || "application/pdf",
        });
        const pdfUrl = URL.createObjectURL(pdfFile);
        if (previewWindow.closed) {
          URL.revokeObjectURL(pdfUrl);
          throw new Error("L'onglet d'aperçu a été fermé.");
        }

        previewWindow.opener = null;
        previewWindow.location.replace(pdfUrl);
        window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 5 * 60 * 1000);
        message.success("Facture ouverte dans le lecteur PDF");
      } catch (error) {
        if (!previewWindow.closed) previewWindow.close();
        message.error(
          error instanceof Error
            ? error.message
            : "Impossible de générer cette facture",
        );
      } finally {
        setPreviewingInvoiceId(null);
      }
    },
    [getRequestHeaders],
  );

  const invoiceColumns = useMemo(
    () => [
      {
        title: "Facture",
        dataIndex: "invoice_number",
        key: "invoice_number",
        render: (invoiceNumber: string, invoice: SubscriberInvoice) => (
          <div className="subscriber-portal__invoice-number">
            <strong>{invoiceNumber}</strong>
            <span>{invoice.plan_name || "Abonnement ELSA GESTION"}</span>
          </div>
        ),
      },
      {
        title: "Émission",
        dataIndex: "invoice_date",
        key: "invoice_date",
        render: formatSubscriptionDate,
      },
      {
        title: "Échéance",
        dataIndex: "due_date",
        key: "due_date",
        render: (date: string, invoice: SubscriberInvoice) => (
          <span className={invoice.display_status === "overdue" ? "is-overdue" : ""}>
            {formatSubscriptionDate(date)}
          </span>
        ),
      },
      {
        title: "Montant",
        dataIndex: "total_amount",
        key: "total_amount",
        align: "right" as const,
        render: (amount: number, invoice: SubscriberInvoice) => (
          <strong>{formatSubscriptionAmount(amount, invoice.currency)}</strong>
        ),
      },
      {
        title: "Reste à payer",
        dataIndex: "balance_due",
        key: "balance_due",
        align: "right" as const,
        render: (amount: number, invoice: SubscriberInvoice) => (
          <span className={amount > 0 ? "subscriber-portal__balance" : ""}>
            {formatSubscriptionAmount(amount, invoice.currency)}
          </span>
        ),
      },
      {
        title: "Statut",
        dataIndex: "display_status",
        key: "display_status",
        render: (status: string) => (
          <Tag color={invoiceStatusColors[status] || "default"}>
            {invoiceStatusLabels[status] || status}
          </Tag>
        ),
      },
      {
        title: "",
        key: "action",
        render: (_: unknown, invoice: SubscriberInvoice) => (
          <Button
            type="text"
            icon={<EyeOutlined />}
            aria-label={`Voir la facture ${invoice.invoice_number}`}
            onClick={() => setSelectedInvoice(invoice)}
          />
        ),
      },
    ],
    [],
  );

  if (loading && !portalData) {
    return (
      <div className="subscriber-portal subscriber-portal--loading">
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  }

  if (!portalData) {
    return (
      <div className="subscriber-portal subscriber-portal--empty">
        <Empty description="Votre souscription n'a pas pu être chargée.">
          <Button icon={<ReloadOutlined />} onClick={() => loadPortal()}>
            Réessayer
          </Button>
        </Empty>
      </div>
    );
  }

  const { company, billing, invoices, available_plans: plans, requests, access } =
    portalData;
  const expiry = company.licence_expire_on || company.trial_ends_at;
  const pendingPlanRequest = requests.some(
    (request) => request.status === "pending" && request.request_type === "plan_change",
  );
  const pendingRenewalRequest = requests.some(
    (request) => request.status === "pending" && request.request_type === "renewal",
  );
  const usagePercent = company.plan_max_users
    ? Math.min(100, Math.round((company.total_users / company.plan_max_users) * 100))
    : 0;
  const accessAlert = access
    ? getAccessAlertContent(access, company.plan_currency, billing)
    : null;

  const overviewTab = (
    <div className="subscriber-portal__tab-content">
      {accessAlert && access?.access_state !== "normal" && (
        <Alert
          type={accessAlert.type}
          showIcon
          icon={<WarningOutlined />}
          message={accessAlert.message}
          description={accessAlert.description}
        />
      )}
      {billing.overdue_invoices > 0 && (
        <Alert
          type="error"
          showIcon
          icon={<WarningOutlined />}
          message={`${formatSubscriptionNumber(billing.overdue_invoices)} facture(s) arrivée(s) à échéance`}
          description={`Le montant en retard est de ${formatSubscriptionAmount(
            billing.overdue_amount,
            company.plan_currency,
          )}. Consultez l'onglet Factures pour le détail.`}
        />
      )}

      <Row gutter={[14, 14]}>
        <Col xs={24} lg={15}>
          <Card className="subscriber-portal__current-plan" bordered={false}>
            <div className="subscriber-portal__section-heading">
              <div>
                <span>Votre formule actuelle</span>
                <h3>{company.plan_name || "Aucun plan attribué"}</h3>
              </div>
              <span className="subscriber-portal__section-marker">
                <SafetyCertificateOutlined />
              </span>
            </div>
            <div className="subscriber-portal__plan-price">
              <strong>
                {formatSubscriptionAmount(
                  company.plan_price,
                  company.plan_currency,
                )}
              </strong>
              <span>
                par mois pour {company.active_warehouse_count || 0} Warehouse(s)
              </span>
            </div>
            {company.plan_description && <Paragraph>{company.plan_description}</Paragraph>}

            <Row gutter={[10, 10]} className="subscriber-portal__limits">
              <Col xs={24} sm={8}>
                <div>
                  <TeamOutlined />
                  <span>Utilisateurs</span>
                  <strong>{getPlanLimit(company.plan_max_users)}</strong>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div>
                  <ShopOutlined />
                  <span>Entreprises</span>
                  <strong>{getPlanLimit(company.plan_max_companies)}</strong>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div>
                  <SafetyCertificateOutlined />
                  <span>Stockage</span>
                  <strong>{getPlanLimit(company.plan_storage_limit, " Go")}</strong>
                </div>
              </Col>
            </Row>

            {company.plan_max_users && (
              <div className="subscriber-portal__usage">
                <div>
                  <span>Utilisation des comptes</span>
                  <strong>
                    {formatSubscriptionNumber(company.total_users)} / {" "}
                    {formatSubscriptionNumber(company.plan_max_users)}
                  </strong>
                </div>
                <Progress
                  percent={usagePercent}
                  showInfo={false}
                  status={usagePercent >= 100 ? "exception" : "normal"}
                />
              </div>
            )}

            <div className="subscriber-portal__features">
              {company.plan_features?.length ? (
                company.plan_features.map((feature, index) => (
                  <Tag key={`${feature}-${index}`} icon={<CheckCircleOutlined />}>
                    {feature}
                  </Tag>
                ))
              ) : (
                <Text type="secondary">Aucune fonctionnalité détaillée.</Text>
              )}
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={9}>
          <Card className="subscriber-portal__actions-card" bordered={false}>
            <div className="subscriber-portal__action-heading">
              <span>Gestion de l’abonnement</span>
              <h3>Faire évoluer votre formule</h3>
            </div>
            <p>
              Votre demande sera transmise à l’équipe ELSA. Aucun changement ni
              débit n’est réalisé automatiquement.
            </p>
            <Button
              block
              type="primary"
              icon={<SwapOutlined />}
              disabled={pendingPlanRequest}
              onClick={() => openRequestModal("plan_change")}
            >
              {pendingPlanRequest
                ? "Changement de plan en attente"
                : "Demander un changement de plan"}
            </Button>
            <Button
              block
              icon={<ClockCircleOutlined />}
              disabled={pendingRenewalRequest}
              onClick={() => openRequestModal("renewal")}
            >
              {pendingRenewalRequest
                ? "Renouvellement en attente"
                : "Demander un renouvellement"}
            </Button>
          </Card>
        </Col>
      </Row>
    </div>
  );

  const invoicesTab = (
    <div className="subscriber-portal__tab-content">
      <div className="subscriber-portal__tab-heading">
        <div>
          <h3>Vos factures de souscription</h3>
          <p>Les montants et échéances concernent uniquement {company.name}.</p>
        </div>
        <Tag color={billing.overdue_invoices > 0 ? "error" : "success"}>
          {billing.overdue_invoices > 0
            ? `${billing.overdue_invoices} en retard`
            : "Aucun retard"}
        </Tag>
      </div>
      <Table
        rowKey="id"
        columns={invoiceColumns}
        dataSource={invoices}
        pagination={{ pageSize: 8, hideOnSinglePage: true }}
        scroll={{ x: 900 }}
        locale={{ emptyText: "Aucune facture de souscription." }}
        rowClassName={(invoice) =>
          invoice.display_status === "overdue" ? "subscriber-portal__overdue-row" : ""
        }
      />
    </div>
  );

  const plansTab = (
    <div className="subscriber-portal__tab-content">
      <div className="subscriber-portal__tab-heading">
        <div>
          <h3>Comparer les formules disponibles</h3>
          <p>Choisissez une formule puis transmettez votre demande à l’équipe ELSA.</p>
        </div>
      </div>
      <Row gutter={[14, 14]}>
        {plans.map((plan) => {
          const isCurrent = plan.id === company.subscription_plan_id;
          return (
            <Col xs={24} md={12} xl={8} key={plan.id}>
              <Card
                className={`subscriber-portal__offer${isCurrent ? " is-current" : ""}`}
                bordered={false}
              >
                <div className="subscriber-portal__offer-heading">
                  <div>
                    <span>{isCurrent ? "Votre formule" : "Formule disponible"}</span>
                    <h3>{plan.name}</h3>
                  </div>
                  {isCurrent && <CheckCircleOutlined />}
                </div>
                <strong className="subscriber-portal__offer-price">
                  Fonctionnalités et limites
                </strong>
                <span>Facturation séparée par Warehouse actif</span>
                <Paragraph ellipsis={{ rows: 2 }}>{plan.description || "—"}</Paragraph>
                <ul>
                  <li>{getPlanLimit(plan.max_users)} utilisateur(s)</li>
                  <li>{getPlanLimit(plan.storage_limit_gb, " Go")} de stockage</li>
                  {plan.features.slice(0, 3).map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
                <Button
                  block
                  type={isCurrent ? "default" : "primary"}
                  disabled={isCurrent || pendingPlanRequest}
                  onClick={() => openRequestModal("plan_change", plan.id)}
                >
                  {isCurrent ? "Plan actuel" : "Demander ce plan"}
                </Button>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );

  const requestsTab = (
    <div className="subscriber-portal__tab-content">
      <div className="subscriber-portal__tab-heading">
        <div>
          <h3>Suivi de vos demandes</h3>
          <p>Retrouvez les demandes transmises et la réponse de l’équipe ELSA.</p>
        </div>
      </div>
      <List
        dataSource={requests}
        locale={{ emptyText: "Aucune demande transmise." }}
        renderItem={(request) => (
          <List.Item
            actions={
              request.status === "pending"
                ? [
                    <Button
                      key="cancel"
                      type="link"
                      danger
                      loading={cancellingRequestId === request.id}
                      onClick={() => cancelRequest(request.id)}
                    >
                      Annuler
                    </Button>,
                  ]
                : undefined
            }
          >
            <List.Item.Meta
              avatar={
                <Avatar
                  icon={
                    request.request_type === "plan_change" ? (
                      <SwapOutlined />
                    ) : (
                      <ClockCircleOutlined />
                    )
                  }
                />
              }
              title={
                <Space wrap>
                  <strong>
                    {request.request_type === "plan_change"
                      ? `Changement vers ${request.requested_plan_name || "un autre plan"}`
                      : `Renouvellement de ${request.requested_duration_months} mois`}
                  </strong>
                  <Tag color={requestStatusColors[request.status]}>
                    {requestStatusLabels[request.status]}
                  </Tag>
                </Space>
              }
              description={
                <div className="subscriber-portal__request-description">
                  <span>Transmise le {formatSubscriptionDate(request.created_at)}</span>
                  {request.message && <span>{request.message}</span>}
                  {request.review_note && (
                    <span className="subscriber-portal__review-note">
                      Réponse : {request.review_note}
                    </span>
                  )}
                </div>
              }
            />
          </List.Item>
        )}
      />
    </div>
  );

  return (
    <div className="subscriber-portal">
      <section className="subscriber-portal__header">
        <div className="subscriber-portal__identity">
          <Avatar shape="square" size={48} icon={<ShopOutlined />} />
          <div>
            <span>ESPACE ABONNÉ</span>
            <Title level={2}>Ma souscription</Title>
            <Text className="subscriber-portal__company-name">
              {company.name}
            </Text>
          </div>
        </div>
        <div className="subscriber-portal__header-actions">
          <SubscriptionStatusBadge status={company.subscription_status} />
          <Button icon={<ReloadOutlined spin={loading} />} onClick={() => loadPortal()}>
            Actualiser
          </Button>
        </div>
      </section>

      {company.subscription_status === "expired" && (
        <Alert
          className="subscriber-portal__status-alert"
          type="error"
          showIcon
          message="Votre souscription est expirée"
          description="Vos données restent protégées. Demandez un renouvellement pour faire réactiver l'accès aux autres modules."
          action={
            <Button
              type="primary"
              danger
              disabled={pendingRenewalRequest}
              onClick={() => openRequestModal("renewal")}
            >
              Demander le renouvellement
            </Button>
          }
        />
      )}

      <section
        className="subscriber-portal__summary"
        aria-label="Synthèse de la souscription"
      >
        <article className="subscriber-portal__summary-item">
          <span className="subscriber-portal__summary-icon">
            <SafetyCertificateOutlined />
          </span>
          <div>
            <span>Plan actuel</span>
            <strong>{company.plan_name || "Non attribué"}</strong>
            <small>
              Facturation {billingFrequencyLabels[company.package_type || "monthly"]} par Warehouse actif
            </small>
          </div>
        </article>
        <article className="subscriber-portal__summary-item">
          <span className="subscriber-portal__summary-icon">
            <CalendarOutlined />
          </span>
          <div>
            <span>Prochaine échéance</span>
            <strong>{formatSubscriptionDate(expiry)}</strong>
            <small>
              {company.days_remaining === null
                ? "Date non définie"
                : company.days_remaining >= 0
                  ? `${company.days_remaining} jour(s) restant(s)`
                  : `Dépassée de ${Math.abs(company.days_remaining)} jour(s)`}
            </small>
          </div>
        </article>
        <article className="subscriber-portal__summary-item">
          <span className="subscriber-portal__summary-icon">
            <TeamOutlined />
          </span>
          <div>
            <span>Utilisateurs</span>
            <strong>{formatSubscriptionNumber(company.total_users)}</strong>
            <small>{formatSubscriptionNumber(company.active_users)} comptes actifs</small>
          </div>
        </article>
        <article
          className={`subscriber-portal__summary-item${
            billing.overdue_amount > 0 ? " is-warning" : ""
          }`}
        >
          <span className="subscriber-portal__summary-icon">
            <FileTextOutlined />
          </span>
          <div>
            <span>Solde à régler</span>
            <strong>
              {formatSubscriptionAmount(
                billing.outstanding_amount,
                company.plan_currency,
              )}
            </strong>
            <small>
              {billing.overdue_invoices
                ? `${billing.overdue_invoices} facture(s) en retard`
                : "Aucune facture en retard"}
            </small>
          </div>
        </article>
      </section>

      <Card className="subscriber-portal__workspace" bordered={false}>
        <Tabs
          items={[
            { key: "overview", label: "Vue d'ensemble", children: overviewTab },
            {
              key: "invoices",
              label: `Factures (${billing.total_invoices})`,
              children: invoicesTab,
            },
            { key: "plans", label: "Plans disponibles", children: plansTab },
            {
              key: "requests",
              label: `Mes demandes (${requests.length})`,
              children: requestsTab,
            },
          ]}
        />
      </Card>

      <Modal
        centered
        className="subscriber-portal-modal subscriber-portal-modal--request"
        open={requestModalOpen}
        title={
          <div className="subscriber-portal__request-title">
            <Avatar icon={requestType === "plan_change" ? <SwapOutlined /> : <ClockCircleOutlined />} />
            <div>
              <strong>
                {requestType === "plan_change"
                  ? "Demander un changement de plan"
                  : "Demander un renouvellement"}
              </strong>
              <span>{company.name}</span>
            </div>
          </div>
        }
        onCancel={() => setRequestModalOpen(false)}
        footer={null}
        destroyOnClose
        width={620}
      >
        <Alert
          type="info"
          showIcon
          message="Cette action transmet une demande"
          description="Votre plan, votre échéance et vos factures ne seront pas modifiés automatiquement."
        />
        <Form form={form} layout="vertical" onFinish={submitRequest}>
          {requestType === "plan_change" ? (
            <Form.Item
              name="requested_plan_id"
              label="Plan souhaité"
              rules={[{ required: true, message: "Sélectionnez un plan" }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Choisir une formule"
                options={plans
                  .filter((plan) => plan.id !== company.subscription_plan_id)
                  .map((plan) => ({
                    value: plan.id,
                    label: `${plan.name} · fonctionnalités et limites`,
                  }))}
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="requested_duration_months"
              label="Durée souhaitée"
              rules={[{ required: true, message: "Sélectionnez une durée" }]}
            >
              <Select
                options={[1, 3, 6, 12, 24, 36].map((months) => ({
                  value: months,
                  label: `${months} mois`,
                }))}
              />
            </Form.Item>
          )}
          <Form.Item name="message" label="Précisions (facultatif)">
            <TextArea
              rows={4}
              maxLength={1000}
              showCount
              placeholder="Ajoutez toute information utile à l'équipe ELSA…"
            />
          </Form.Item>
          <div className="subscriber-portal__request-footer">
            <Button onClick={() => setRequestModalOpen(false)}>Annuler</Button>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SendOutlined />}
              loading={submitting}
            >
              Transmettre la demande
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        centered
        className="subscriber-portal-modal subscriber-portal-modal--invoice"
        open={Boolean(selectedInvoice)}
        title={
          <div className="subscriber-portal__request-title">
            <Avatar shape="square" icon={<FileTextOutlined />} />
            <div>
              <strong>Détail de la facture</strong>
              <span>{selectedInvoice?.invoice_number || company.name}</span>
            </div>
          </div>
        }
        onCancel={() => setSelectedInvoice(null)}
        footer={[
          <Button key="close" onClick={() => setSelectedInvoice(null)}>
            Fermer
          </Button>,
          <Button
            key="open-pdf"
            type="primary"
            icon={<FilePdfOutlined />}
            loading={previewingInvoiceId === selectedInvoice?.id}
            onClick={() => selectedInvoice && openInvoicePdf(selectedInvoice)}
          >
            Ouvrir / imprimer
          </Button>,
        ]}
      >
        {selectedInvoice && (
          <Descriptions column={1} colon={false}>
            <Descriptions.Item label="Numéro">
              {selectedInvoice.invoice_number}
            </Descriptions.Item>
            <Descriptions.Item label="Période">
              {formatSubscriptionDate(selectedInvoice.period_start)} – {" "}
              {formatSubscriptionDate(selectedInvoice.period_end)}
            </Descriptions.Item>
            <Descriptions.Item label="Échéance">
              {formatSubscriptionDate(selectedInvoice.due_date)}
            </Descriptions.Item>
            <Descriptions.Item label="Montant">
              {formatSubscriptionAmount(
                selectedInvoice.total_amount,
                selectedInvoice.currency,
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Reste à payer">
              {formatSubscriptionAmount(
                selectedInvoice.balance_due,
                selectedInvoice.currency,
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Statut">
              <Tag
                color={
                  invoiceStatusColors[selectedInvoice.display_status] || "default"
                }
              >
                {invoiceStatusLabels[selectedInvoice.display_status] ||
                  selectedInvoice.display_status}
              </Tag>
            </Descriptions.Item>
            {selectedInvoice.replaces_invoice_number && (
              <Descriptions.Item label="Corrige la facture">
                {selectedInvoice.replaces_invoice_number}
              </Descriptions.Item>
            )}
            {selectedInvoice.replacement_invoice_number && (
              <Descriptions.Item label="Remplacée par">
                {selectedInvoice.replacement_invoice_number}
              </Descriptions.Item>
            )}
            {selectedInvoice.correction_reason && (
              <Descriptions.Item label="Motif de correction">
                {selectedInvoice.correction_reason}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default SubscriberSubscriptionPortal;
